/**
 * Site Context
 *
 * Defines the React context for site state management.
 * Provides interfaces for site state, errors, and context value.
 *
 * Requirements: 2.1 - THE Site_API_Client SHALL be implemented as a separate module from other application code
 */

import { createContext } from 'react';
import type { Site } from '../models/domainModels';

/**
 * Represents the current state of the site context.
 * All properties are readonly for immutability.
 */
export interface SiteState {
  readonly site: Site | null;
  readonly isLoading: boolean;
  readonly error: SiteError | null;
  readonly keys: readonly string[];
  readonly isKeysLoading: boolean;
  readonly keysError: KeysError | null;
}

/**
 * Discriminated union type for site-related errors.
 * - 'not_found': Site with the given siteId does not exist (404 response)
 * - 'api_error': Generic API error with a message
 */
export type SiteError =
  | { readonly type: 'not_found'; readonly siteId: string }
  | { readonly type: 'api_error'; readonly message: string };

/**
 * Discriminated union type for key-related errors.
 * - 'fetch_failed': Error occurred while fetching keys
 * - 'create_failed': Error occurred while creating a new key
 */
export type KeysError =
  | { readonly type: 'fetch_failed'; readonly message: string }
  | { readonly type: 'create_failed'; readonly message: string };

/**
 * The complete context value exposed to consumers.
 * Extends SiteState with actions for loading and clearing site data,
 * as well as key management functions.
 */
export interface SiteContextValue extends SiteState {
  readonly loadSite: (siteId: string) => Promise<void>;
  readonly clearSite: () => void;
  readonly refreshKeys: () => Promise<void>;
  readonly createKey: () => Promise<string>;
  readonly ensureKey: () => Promise<string>;
}

/**
 * Initial state for the site context.
 * No site loaded, not loading, no errors, no keys.
 */
export const initialSiteState: SiteState = {
  site: null,
  isLoading: false,
  error: null,
  keys: [],
  isKeysLoading: false,
  keysError: null,
};

/**
 * React context for site state management.
 * Consumers should use the useSite hook rather than consuming this context directly.
 */
export const SiteContext = createContext<SiteContextValue | null>(null);
